import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Shield,
  Loader2,
  Search,
  User,
  Plus,
  Minus,
  Save,
  ChevronRight,
  Check,
  X,
  UserCog,
  AlertCircle,
  Eye
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Switch } from "./ui/switch";

// Icon mapper
const getIcon = (iconName) => {
  if (!iconName) return LucideIcons.Circle;
  const Icon = LucideIcons[iconName];
  return Icon || LucideIcons.Circle;
};

export default function UserPermissionsManager() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [menuStructure, setMenuStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [usersWithCustomPerms, setUsersWithCustomPerms] = useState([]);
  
  // Permission form data
  const [permisosAdicionales, setPermisosAdicionales] = useState({});
  const [permisosDenegados, setPermisosDenegados] = useState({});
  const [tipoVisibility, setTipoVisibility] = useState("");
  const [tipoVisibilityOriginal, setTipoVisibilityOriginal] = useState("");
  const [savingTipo, setSavingTipo] = useState(false);
  
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, menusRes, customPermsRes] = await Promise.all([
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/system/menus`, getAuthHeaders()),
        axios.get(`${API}/users/with-custom-permissions`, getAuthHeaders())
      ]);
      
      setUsers(usersRes.data || []);
      setFilteredUsers(usersRes.data || []);
      setMenuStructure(menusRes.data || []);
      setUsersWithCustomPerms(customPermsRes.data || []);
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      setFilteredUsers(users.filter(u => 
        u.name?.toLowerCase().includes(term) || 
        u.email?.toLowerCase().includes(term) ||
        u.role?.toLowerCase().includes(term)
      ));
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const openUserDialog = async (user) => {
    setSelectedUser(user);
    
    // Load current user permissions
    try {
      const res = await axios.get(`${API}/users/${user.id}/permissions`, getAuthHeaders());
      setPermisosAdicionales(res.data.permisos_adicionales || {});
      setPermisosDenegados(res.data.permisos_denegados || {});
    } catch (err) {
      setPermisosAdicionales({});
      setPermisosDenegados({});
    }
    
    // Load renovaciones tipo visibility
    try {
      const userRes = await axios.get(`${API}/users`, getAuthHeaders());
      const fullUser = (userRes.data || []).find(u => u.id === user.id);
      const tv = fullUser?.renovaciones_tipo_visibility || "";
      setTipoVisibility(tv);
      setTipoVisibilityOriginal(tv);
    } catch (err) {
      setTipoVisibility("");
      setTipoVisibilityOriginal("");
    }
    
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      // Save menu permissions
      await axios.put(`${API}/users/${selectedUser.id}/permissions`, {
        permisos_adicionales: permisosAdicionales,
        permisos_denegados: permisosDenegados
      }, getAuthHeaders());
      
      // Save tipo visibility SOLO si el admin lo cambió (evita revocaciones
      // silenciosas cuando se guardan otros permisos)
      if ((tipoVisibility || "") !== (tipoVisibilityOriginal || "")) {
        await axios.put(`${API}/control-renovaciones/user/${selectedUser.id}/tipo-visibility`, {
          renovaciones_tipo_visibility: tipoVisibility || null
        }, getAuthHeaders());
      }
      
      toast.success(`Permisos de ${selectedUser.name} actualizados`);
      setShowDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar permisos");
    } finally {
      setSaving(false);
    }
  };

  const toggleAdicional = (menuId) => {
    setPermisosAdicionales(prev => {
      const newPerms = { ...prev };
      if (newPerms[menuId]?.access) {
        delete newPerms[menuId];
      } else {
        newPerms[menuId] = { access: true };
      }
      return newPerms;
    });
    // Remove from denied if adding to additional
    if (!permisosAdicionales[menuId]?.access) {
      setPermisosDenegados(prev => {
        const newPerms = { ...prev };
        delete newPerms[menuId];
        return newPerms;
      });
    }
  };

  const toggleDenegado = (menuId) => {
    setPermisosDenegados(prev => {
      const newPerms = { ...prev };
      if (newPerms[menuId]?.denied) {
        delete newPerms[menuId];
      } else {
        newPerms[menuId] = { denied: true };
      }
      return newPerms;
    });
    // Remove from additional if denying
    if (!permisosDenegados[menuId]?.denied) {
      setPermisosAdicionales(prev => {
        const newPerms = { ...prev };
        delete newPerms[menuId];
        return newPerms;
      });
    }
  };

  const hasCustomPermissions = (userId) => {
    return usersWithCustomPerms.some(u => u.id === userId);
  };

  const countCustomPermissions = (userId) => {
    const user = usersWithCustomPerms.find(u => u.id === userId);
    if (!user) return { adicionales: 0, denegados: 0 };
    return {
      adicionales: Object.keys(user.permisos_adicionales || {}).length,
      denegados: Object.keys(user.permisos_denegados || {}).length
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800 dark:text-blue-200">Permisos Adicionales por Usuario</h3>
            <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
              Aquí puede otorgar o revocar permisos específicos a usuarios individuales sin modificar sus roles.
              Los permisos adicionales se suman a los del rol, mientras que los denegados los restringen.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar usuario por nombre, email o rol..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="permisos-search-input"
        />
      </div>

      {/* Users with custom permissions summary */}
      {usersWithCustomPerms.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <UserCog className="w-4 h-4" />
              Usuarios con Permisos Personalizados ({usersWithCustomPerms.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {usersWithCustomPerms.map(u => (
                <Badge 
                  key={u.id} 
                  variant="outline" 
                  className="cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-800/50"
                  onClick={() => openUserDialog(u)}
                >
                  {u.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsers.map((user) => {
          const customPerms = countCustomPermissions(user.id);
          const hasCustom = hasCustomPermissions(user.id);
          
          return (
            <Card 
              key={user.id} 
              className={`border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                hasCustom ? 'border-amber-300 dark:border-amber-700' : 'border-slate-200 dark:border-slate-700'
              }`}
              onClick={() => openUserDialog(user)}
              data-testid={`permisos-user-card-${user.email}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    hasCustom ? 'bg-amber-100 dark:bg-amber-900' : 'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    <User className={`w-5 h-5 ${hasCustom ? 'text-amber-600' : 'text-slate-600 dark:text-slate-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">{user.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{user.role}</Badge>
                      {hasCustom && (
                        <Badge className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                          +{customPerms.adicionales} -{customPerms.denegados}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No se encontraron usuarios
        </div>
      )}

      {/* Edit User Permissions Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-[#0C314C]" />
              Permisos de {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              Rol actual: <Badge variant="outline">{selectedUser?.role}</Badge>
              <br />
              Configure permisos adicionales (otorgar) o denegados (revocar) para este usuario.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {/* Renovaciones Tipo Visibility Section */}
            <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4 text-indigo-600" />
                <span className="font-medium text-sm text-indigo-800 dark:text-indigo-200">Visibilidad de Renovaciones por Tipo</span>
              </div>
              <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-3">
                Permite al usuario ver y editar renovaciones de todos los ejecutivos con un tipo específico en Carta de Equipos. No podrá eliminar registros.
              </p>
              <div className="flex items-center gap-4">
                <Switch
                  checked={!!tipoVisibility}
                  onCheckedChange={(checked) => {
                    setTipoVisibility(checked ? "Individual/Pymes" : "");
                  }}
                  data-testid="tipo-visibility-switch"
                />
                {tipoVisibility ? (
                  <Select value={tipoVisibility} onValueChange={setTipoVisibility}>
                    <SelectTrigger className="w-56" data-testid="tipo-visibility-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Individual/Pymes">Individual / Pymes</SelectItem>
                      <SelectItem value="Corporativo/Empresarial">Corporativo / Empresarial</SelectItem>
                      <SelectItem value="Salud Local">Salud Local</SelectItem>
                      <SelectItem value="Salud Internacional">Salud Internacional</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-sm text-slate-500">Desactivado — solo ve sus propias renovaciones</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Plus className="w-4 h-4" />
                  <span className="font-medium text-sm">Permisos Adicionales</span>
                </div>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Accesos extra que se suman al rol
                </p>
              </div>
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                  <Minus className="w-4 h-4" />
                  <span className="font-medium text-sm">Permisos Denegados</span>
                </div>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  Accesos que se restringen del rol
                </p>
              </div>
            </div>

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {menuStructure.map((menu) => {
                  const MenuIcon = getIcon(menu.icon);
                  return (
                    <div key={menu.id} className="border rounded-lg dark:border-slate-700 overflow-hidden">
                      {/* Menu Header */}
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-[#0C314C] flex items-center justify-center">
                            <MenuIcon className="w-4 h-4 text-[#45E69A]" />
                          </div>
                          <span className="font-medium text-slate-900 dark:text-white">{menu.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleAdicional(menu.id)}
                            className={`p-1.5 rounded transition-colors ${
                              permisosAdicionales[menu.id]?.access 
                                ? 'bg-green-500 text-white' 
                                : 'bg-slate-200 dark:bg-slate-700 text-slate-500 hover:bg-green-100'
                            }`}
                            title="Otorgar acceso adicional"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleDenegado(menu.id)}
                            className={`p-1.5 rounded transition-colors ${
                              permisosDenegados[menu.id]?.denied 
                                ? 'bg-red-500 text-white' 
                                : 'bg-slate-200 dark:bg-slate-700 text-slate-500 hover:bg-red-100'
                            }`}
                            title="Denegar acceso"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Submenus */}
                      <div className="divide-y divide-slate-100 dark:divide-slate-700">
                        {menu.submenus?.map((submenu) => {
                          const SubmenuIcon = getIcon(submenu.icon);
                          return (
                            <div 
                              key={submenu.id}
                              className="flex items-center justify-between p-3 pl-14 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                            >
                              <div className="flex items-center gap-2">
                                <ChevronRight className="w-3 h-3 text-slate-400" />
                                <SubmenuIcon className="w-4 h-4 text-slate-500" />
                                <span className="text-sm text-slate-700 dark:text-slate-300">{submenu.label}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleAdicional(submenu.id)}
                                  className={`p-1 rounded transition-colors ${
                                    permisosAdicionales[submenu.id]?.access 
                                      ? 'bg-green-500 text-white' 
                                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500 hover:bg-green-100'
                                  }`}
                                  title="Otorgar acceso adicional"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => toggleDenegado(submenu.id)}
                                  className={`p-1 rounded transition-colors ${
                                    permisosDenegados[submenu.id]?.denied 
                                      ? 'bg-red-500 text-white' 
                                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500 hover:bg-red-100'
                                  }`}
                                  title="Denegar acceso"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              data-testid="permisos-save-button"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Save className="w-4 h-4 mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
